import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  CheckCircle2,
  Package,
  BarChart3,
  RefreshCw,
  TrendingUp,
  Zap,
  ChevronRight,
  ShoppingCart,
  Users,
  Briefcase,
  Send,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

export const AmazonIntegration = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    businessType: '',
    monthlyOrders: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const features = [
    {
      icon: RefreshCw,
      title: 'Order Synchronization',
      description: 'Automatically sync orders from Amazon to your system in real-time.',
      color: 'from-blue-500 to-indigo-600',
    },
    {
      icon: Package,
      title: 'Inventory Management',
      description: 'Keep inventory levels synchronized across all your sales channels.',
      color: 'from-teal-500 to-cyan-600',
    },
    {
      icon: TrendingUp,
      title: 'Dynamic Pricing',
      description: 'Automate pricing updates based on market conditions and demand.',
      color: 'from-green-500 to-emerald-600',
    },
    {
      icon: BarChart3,
      title: 'Analytics Dashboard',
      description: 'Get detailed insights into sales performance and customer behavior.',
      color: 'from-purple-500 to-pink-600',
    },
    {
      icon: Zap,
      title: 'AI-Powered Insights',
      description: 'Leverage AI to predict trends and optimize your Amazon business.',
      color: 'from-orange-500 to-red-600',
    },
    {
      icon: ShoppingCart,
      title: 'Seller Support',
      description: 'Dedicated support team to help you succeed on Amazon.',
      color: 'from-indigo-500 to-purple-600',
    },
  ];

  const pricingPlans = [
    {
      name: 'Starter',
      price: '$99',
      period: '/month',
      description: 'Perfect for new sellers',
      features: [
        'Up to 1,000 orders/month',
        'Basic order sync',
        'Inventory management',
        'Email support',
      ],
      highlighted: false,
    },
    {
      name: 'Growth',
      price: '$199',
      period: '/month',
      description: 'For growing businesses',
      features: [
        'Up to 10,000 orders/month',
        'Advanced order sync',
        'Multi-channel inventory',
        'Pricing automation',
        'Analytics dashboard',
        'Priority support',
      ],
      highlighted: true,
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: 'Contact us',
      description: 'For large-scale operations',
      features: [
        'Unlimited orders',
        'Custom integrations',
        'AI insights',
        'Dedicated account manager',
        '24/7 support',
        'SLA guarantee',
      ],
      highlighted: false,
    },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      console.log('Form submitted:', formData);
      toast.success('Quote request submitted! We\'ll contact you within 24 hours.');
      
      setFormData({
        name: '',
        email: '',
        businessType: '',
        monthlyOrders: '',
        message: '',
      });
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 hero-gradient mesh-gradient overflow-hidden">
        <div className="absolute top-20 right-10 w-72 h-72 bg-blue-400/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-20 w-96 h-96 bg-purple-400/20 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block px-4 py-1.5 rounded-full bg-blue-50 text-blue-600 text-sm font-medium mb-4">
              Amazon SP-API
            </span>
            <h1 className="text-4xl md:text-6xl font-bold font-['Outfit'] tracking-tighter mb-6">
              Automate Your Amazon{' '}
              <span className="gradient-text">Selling Operations</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 mb-8 max-w-2xl mx-auto leading-relaxed">
              We provide Amazon SP-API integrations for sellers to automate order management, inventory sync, pricing updates, and analytics.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => document.getElementById('quote-form').scrollIntoView({ behavior: 'smooth' })}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-full px-8 py-3 font-medium shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all"
              >
                Get Started
                <ArrowRight className="ml-2" size={18} />
              </Button>
              <Button
                variant="outline"
                className="rounded-full px-8 py-3 font-medium bg-white"
                asChild
              >
                <Link to="/amazon-data-protection-policy">Data Protection Policy</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Service Description Section */}
      <section className="relative py-24 bg-white overflow-hidden">
        <div className="absolute top-20 left-0 w-72 h-72 bg-blue-200/10 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Content */}
            <div>
              <div className="mb-8">
                <span className="inline-block px-4 py-1.5 rounded-full bg-blue-50 text-blue-600 text-sm font-medium mb-4">
                  What We Provide
                </span>
                <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight mb-6">
                  Complete Amazon Integration Solution
                </h2>
              </div>

              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                JNANIT provides comprehensive Amazon SP-API integrations for sellers to automate order management, 
                inventory synchronization, dynamic pricing updates, and advanced analytics. Our solutions are designed 
                to help Amazon sellers scale their operations efficiently while reducing manual work and improving decision-making 
                through AI-powered insights.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                When Amazon customer PII is approved and required, we process it only for narrow operational purposes such as merchant-fulfilled shipping, tax invoicing, tax remittance, or legal/regulatory requirements. We do not use Amazon customer PII for marketing, customer profiling, resale, unrelated analytics, or cross-seller aggregation.
              </p>

              <div className="space-y-4 mb-8">
                <h3 className="text-xl font-bold font-['Outfit'] text-slate-900">Key Benefits:</h3>
                <div className="space-y-3">
                  {[
                    'Eliminate manual data entry and reduce errors by 95%',
                    'Real-time synchronization across all channels',
                    'Save 20+ hours per week on administrative tasks',
                    'AI-powered insights for better decision making',
                    'Scalable architecture grows with your business',
                    'Dedicated support team available 24/7'
                  ].map((benefit, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-1">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                      </div>
                      <span className="text-slate-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Button
                onClick={() => document.getElementById('quote-form').scrollIntoView({ behavior: 'smooth' })}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-full px-8 py-3 font-medium shadow-lg shadow-blue-500/25"
              >
                Get Started Today
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </div>

            {/* Right Side - Visual */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-indigo-600/20 rounded-3xl blur-2xl" />
              <Card className="relative bg-white rounded-3xl shadow-2xl border-0 overflow-hidden">
                <CardContent className="p-8">
                  <div className="space-y-6">
                    {/* Feature Card 1 */}
                    <div className="p-6 bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl border border-blue-200">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center flex-shrink-0">
                          <RefreshCw className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <h4 className="font-bold text-slate-900 mb-2">Order Management</h4>
                          <p className="text-sm text-slate-600">Automate order processing and fulfillment</p>
                        </div>
                      </div>
                    </div>

                    {/* Feature Card 2 */}
                    <div className="p-6 bg-gradient-to-br from-green-50 to-green-100/50 rounded-2xl border border-green-200">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-green-600 to-green-700 flex items-center justify-center flex-shrink-0">
                          <Package className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <h4 className="font-bold text-slate-900 mb-2">Inventory Sync</h4>
                          <p className="text-sm text-slate-600">Keep stock levels synchronized across channels</p>
                        </div>
                      </div>
                    </div>

                    {/* Feature Card 3 */}
                    <div className="p-6 bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-2xl border border-purple-200">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-600 to-purple-700 flex items-center justify-center flex-shrink-0">
                          <TrendingUp className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <h4 className="font-bold text-slate-900 mb-2">Dynamic Pricing</h4>
                          <p className="text-sm text-slate-600">Automate pricing based on market conditions</p>
                        </div>
                      </div>
                    </div>

                    {/* Feature Card 4 */}
                    <div className="p-6 bg-gradient-to-br from-orange-50 to-orange-100/50 rounded-2xl border border-orange-200">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-orange-600 to-orange-700 flex items-center justify-center flex-shrink-0">
                          <BarChart3 className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <h4 className="font-bold text-slate-900 mb-2">Analytics Dashboard</h4>
                          <p className="text-sm text-slate-600">Gain insights into your sales performance</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative py-24 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl -translate-y-1/2" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-blue-50 text-blue-600 text-sm font-medium mb-4">
              Features
            </span>
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight">
              Powerful Tools for Amazon Sellers
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="group hover:shadow-xl hover:border-blue-200 transition-all duration-300 border border-slate-200 bg-white">
                  <CardContent className="pt-8">
                    <div className={`inline-block p-3 rounded-lg bg-gradient-to-br ${feature.color} text-white mb-6 group-hover:scale-110 transition-transform`}>
                      <Icon size={24} />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-3 font-['Outfit']">
                      {feature.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="relative py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-blue-50 text-blue-600 text-sm font-medium mb-4">
              Pricing
            </span>
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight mb-4">
              Transparent Pricing Plans
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Choose the plan that fits your business needs. Always transparent, no hidden fees.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, index) => (
              <Card 
                key={index} 
                className={`relative transition-all duration-300 border-2 ${
                  plan.highlighted 
                    ? 'ring-2 ring-blue-600 shadow-2xl md:scale-105 border-blue-200 bg-gradient-to-br from-white to-blue-50' 
                    : 'hover:shadow-lg border-slate-200 hover:border-slate-300'
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-1.5 rounded-full text-sm font-semibold shadow-lg">
                      Most Popular
                    </span>
                  </div>
                )}
                <CardHeader>
                  <CardTitle className="text-2xl font-bold text-slate-900 font-['Outfit']">
                    {plan.name}
                  </CardTitle>
                  <CardDescription className="text-slate-600">{plan.description}</CardDescription>
                  <div className="mt-6">
                    <span className="text-5xl font-bold text-slate-900">
                      {plan.price}
                    </span>
                    <span className="text-slate-600 ml-2 font-medium">
                      {plan.period}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <Button 
                    className={`w-full mb-8 font-medium rounded-full ${
                      plan.highlighted 
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg shadow-blue-500/25' 
                        : 'bg-slate-100 hover:bg-slate-200 text-slate-900'
                    }`}
                    onClick={() => document.getElementById('quote-form').scrollIntoView({ behavior: 'smooth' })}
                  >
                    Get Started
                    <ArrowRight className="ml-2" size={16} />
                  </Button>
                  <ul className="space-y-4">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle2 className="text-green-500 flex-shrink-0 mt-0.5" size={20} />
                        <span className="text-slate-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Request Form Section */}
      <section id="quote-form" className="relative py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-blue-500/20 text-blue-300 text-sm font-medium mb-4 border border-blue-500/30">
              Get Started
            </span>
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-white mb-4">
              Request a Quote or Demo
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Get a customized proposal based on your specific needs. Our team will contact you within 24 hours.
            </p>
          </div>

          <Card className="bg-white shadow-2xl">
            <CardContent className="pt-10">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Name */}
                  <div>
                    <Label htmlFor="name" className="block text-slate-900 font-semibold mb-2">
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <Label htmlFor="email" className="block text-slate-900 font-semibold mb-2">
                      Business Email *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                    />
                  </div>

                  {/* Business Type */}
                  <div>
                    <Label htmlFor="businessType" className="block text-slate-900 font-semibold mb-2">
                      Business Type *
                    </Label>
                    <select
                      id="businessType"
                      name="businessType"
                      value={formData.businessType}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                    >
                      <option value="">Select Business Type</option>
                      <option value="sole-proprietor">Sole Proprietor</option>
                      <option value="small-business">Small Business (1-10 employees)</option>
                      <option value="medium-business">Medium Business (11-50 employees)</option>
                      <option value="large-business">Large Business (50+ employees)</option>
                      <option value="agency">Agency/Consultant</option>
                    </select>
                  </div>

                  {/* Monthly Orders */}
                  <div>
                    <Label htmlFor="monthlyOrders" className="block text-slate-900 font-semibold mb-2">
                      Estimated Monthly Orders *
                    </Label>
                    <select
                      id="monthlyOrders"
                      name="monthlyOrders"
                      value={formData.monthlyOrders}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                    >
                      <option value="">Select Range</option>
                      <option value="0-100">0 - 100</option>
                      <option value="101-500">101 - 500</option>
                      <option value="501-1000">501 - 1,000</option>
                      <option value="1001-5000">1,001 - 5,000</option>
                      <option value="5001-10000">5,001 - 10,000</option>
                      <option value="10000+">10,000+</option>
                    </select>
                  </div>
                </div>

                {/* Message */}
                <div>
                  <Label htmlFor="message" className="block text-slate-900 font-semibold mb-2">
                    Tell us about your needs
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Describe your specific requirements, challenges, or timeline..."
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={5}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all resize-none"
                  />
                </div>

                {/* Submit Button */}
                <div>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-3 rounded-lg transition-all shadow-lg hover:shadow-xl"
                  >
                    {isSubmitting ? 'Submitting...' : 'Request a Quote'}
                    <Send className="ml-2" size={16} />
                  </Button>
                </div>

                <p className="text-sm text-slate-600 text-center">
                  * Required fields. We respect your privacy and will only use your information to provide you with quotations and updates about our services.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="relative py-24 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-blue-50 text-blue-600 text-sm font-medium mb-4">
              FAQ
            </span>
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight">
              Common Questions
            </h2>
          </div>
          
          <div className="space-y-4">
            {[
              {
                question: 'How does the SP-API integration work?',
                answer: 'Our integration uses Amazon\'s official Selling Partner API to securely connect your Amazon seller account with our platform. This allows real-time synchronization of orders, inventory, and pricing without manual intervention.'
              },
              {
                question: 'What\'s included in the pricing?',
                answer: 'All plans include order synchronization, inventory management, basic analytics, and customer support. Higher plans add advanced features like AI insights, dynamic pricing, and priority support.'
              },
              {
                question: 'Is there a setup fee?',
                answer: 'No hidden setup fees! The monthly subscription covers everything. For enterprise customers, custom implementation services are available at an additional cost.'
              },
              {
                question: 'Can I cancel anytime?',
                answer: 'Yes! Our plans are month-to-month with no long-term contracts. You can upgrade, downgrade, or cancel your subscription at any time.'
              },
            ].map((item, index) => (
              <Card key={index} className="border border-slate-200 hover:border-blue-200 transition-all hover:shadow-md">
                <CardContent className="pt-6">
                  <h3 className="text-lg font-semibold text-slate-900 flex items-center justify-between mb-4 font-['Outfit']">
                    {item.question}
                    <ChevronRight size={20} className="text-slate-400" />
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {item.answer}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-24 bg-white overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400/20 rounded-full blur-3xl" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <Card className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 border-0 shadow-2xl">
            <CardContent className="pt-16 pb-16 text-center">
              <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-white mb-6">
                Ready to Scale Your Amazon Business?
              </h2>
              <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto leading-relaxed">
                Join hundreds of sellers who have automated their Amazon operations with our SP-API solutions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  className="bg-white text-blue-600 hover:bg-slate-50 font-semibold rounded-full px-8"
                  onClick={() => document.getElementById('quote-form').scrollIntoView({ behavior: 'smooth' })}
                >
                  Book a Demo
                  <ArrowRight className="ml-2" size={16} />
                </Button>
                <Button 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/20 font-semibold rounded-full px-8"
                  asChild
                >
                  <Link to="/contact">Contact Us</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default AmazonIntegration;
